import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import classnames from 'classnames'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { getAttributeCreatorFor, Types, Icons } from '@leverege/api-attributes'
import { Button, Label, Checkbox, Selector } from '@leverege/ui-elements'

import BaseAttributeEditor from './BaseAttributeEditor.jsx'
import { 
  TextInput, Flex, LabelGroup, Section, Tags, Icon,
  FontIconSelector, ColorSelector, JsonEditorDialog, InsetSection, 
} from '~/views'

import style from './style.css'
import { BlueprintActions } from '../../actions/index.js'

const RelationshipDesc = {
  oneToOne : 'This device will be referenced as the only TGT by one SRC instance',
  manyToOne : 'This device will be referenced as the TGT by many SRC instances',
  oneToMany : 'This device will be one of many devices referenced as the TGT by one SRC instance',
  manyToMany : 'This device will be one of many devices referenced as the TGT by many SRC instances'
}

class ParentRelationshipAttributeEditor extends React.Component {

  static propTypes = {
    attribute : PropTypes.object,
    blueprint : PropTypes.object,
    currentTags : PropTypes.array,
  }

  constructor( props ) {
    super( props )
    this.state = {
      showJsonEditor : false,
    }

    this.changePath = BaseAttributeEditor.ChangePath
  }

  onFontSelected = ( font ) => {
    this.changePath( 'icon/font', font )
  }

  onColorSelected = ( color ) => {
    this.changePath( 'icon/fontColor', color.rgb )
  }

  onEditMetadata = ( metadata ) => { 
    this.changePath( 'metadata', metadata, false )
    this.setState( { showJsonEditor : false } ) 
  }

  getDesc = ( attr, tgt ) => {
    const { relationshipType } = attr
    const { blueprint, blueprints } = this.props
    
    const sBpName = blueprints?.index?.[attr.sourceBlueprintId]?.name ? 
      blueprints.index[attr.sourceBlueprintId].name :
      attr.sourceBlueprintId

    let str = RelationshipDesc[relationshipType]
    str = str.replace( /SRC/g, sBpName )
    str = str.replace( /TGT/g, blueprint.name )
    str = str.replace( /ATTR/g, attr.name ?? attr.path )
    return str
  }

  componentDidMount() {
    const { dispatch, blueprint } = this.props
    dispatch( BlueprintActions.listBlueprints( blueprint.projectId ) )
  }

  render() {
    const { showJsonEditor } = this.state 
    const { attribute : attr, typeName, currentTags, blueprints } = this.props 

    if ( attr == null ) {
      return <div />
    }

    const attrCreator = getAttributeCreatorFor( attr.type )
    const attrIcon = Icons.getIcon( attr )
 
    const popover = (
      <Popover className={style.popover} style={{ minWidth : '300px' }} id="icon" title="Set Icon URL">
        <LabelGroup>
          <label>URL</label>
          <TextInput
            hint="Icon URL..."
            value={attr.icon && attr.icon.url} 
            changeOnReturn 
            onChange={( v, err ) => { this.changePath( 'icon/url', v, err ) }} />
        </LabelGroup> 
        <div style={{ textAlign : 'center' }}>OR</div>
        <div className={style.fontOptions}>
          <LabelGroup>
            <label>Font</label>
            <FontIconSelector style={{ marginRight : '10px' }} onSelect={this.onFontSelected} font={attrIcon?.fontClass} />
            <ColorSelector onChangeComplete={this.onColorSelected} />
          </LabelGroup>
        </div>
      </Popover>
    )

    const icon = (
      <OverlayTrigger key="icon" rootClose trigger="click" placement="left" overlay={popover}>
        <div className={classnames( style.iconHolder, attrIcon && style.hasIcon )} style={{ width : '40px', height : '40px' }}>
          {attrIcon == null ? <div className={style.noIcon}>SET ICON</div> : <Icon className={style.icon} icon={attrIcon} />}
        </div>
      </OverlayTrigger> 
    )

    let stringifiedMetadata
    if ( attr.metadata ) {
      stringifiedMetadata = JSON.stringify( attr.metadata, null, 2 )
    }
    const sBpName = blueprints?.index?.[attr.sourceBlueprintId]?.name ? 
      `${blueprints.index[attr.sourceBlueprintId].name} - ${attr.sourceBlueprintId}` :
      attr.sourceBlueprintId

    return (
      <Section title={typeName || attrCreator?.name()} subtitle={attr.path} titleOnTop right={icon}>
        <Flex variant="vSpace" align="stretch" direction="column">
          <LabelGroup>
            <label>Name</label>
            <TextInput
              value={attr.name} 
              changeOnReturn
              size={10}
              onChange={( v, err ) => { this.changePath( 'name', v, err ) }} />
          </LabelGroup>
          <LabelGroup>
            <label>Description</label>
            <TextInput
              value={attr.desc}
              changeOnReturn
              size={10}
              onChange={( v, err ) => { this.changePath( 'desc', v, err ) }} />
          </LabelGroup>
          <LabelGroup>
            <label>Parent Path</label>
            <TextInput
              value={attr.parentPath} 
              disabled />
          </LabelGroup>
  
          {attr.relationshipType === 'oneToOne' && (
            <LabelGroup>
              <label>Forward Data</label>
              <Checkbox
                className={style.forwardData}
                value={attr.forwardData}
                onChange={evt => this.changePath( 'forwardData', evt.value )} />
            </LabelGroup>
          )}

          <LabelGroup>
            <label>Tags</label>
            <Tags
              tags={attr.tags}
              autofocus={false}
              suggestions={currentTags}
              tagAdded={( tag, arr ) => { this.changePath( 'tags', arr, false ) }}
              tagRemoved={( tag, arr ) => { this.changePath( 'tags', arr, false ) }} />
          </LabelGroup>

          <Flex variant="noSpace">
            <LabelGroup style={{ flexBasis : '200px', marginRight : '8px' }}>
              <Label>Source</Label>
              <Selector
                values={Types.Source.values()}
                value={attr.source}
                onChange={( value ) => { this.changePath( 'source', value.value, false ); }} />
            </LabelGroup>

            <Flex direction="row">
              <Checkbox
                value={attr.controllable} 
                onChange={( evt ) => { this.changePath( 'controllable', evt.value, false ) }} />
              Controllable
            </Flex>

          </Flex>

          <LabelGroup>
            <label>Metadata</label>
            <Flex>
              <Button 
                variant="secondarySmall"
                className={style.addMetadataButton}
                onClick={() => this.setState( { showJsonEditor : true } )}>
                {stringifiedMetadata ? 'Edit' : 'Add'}
              </Button>
            </Flex>
          </LabelGroup>
          
          <InsetSection className={style.inset}>
            <div className={stringifiedMetadata ? style.metadata : style.noMetadata}>
              {stringifiedMetadata || 'No Metadata' }
            </div> 
          </InsetSection>
          <Flex>
            <JsonEditorDialog 
              title="Relationship Attribute Metadata"
              message="Enter the JSON object for the relationship attribute metadata"
              show={showJsonEditor}
              onOk={this.onEditMetadata}
              onCancel={() => this.setState( { showJsonEditor : false } )}
              value={attr.metadata} />
          </Flex>

          <LabelGroup>
            <label>Source BP</label>
            <TextInput disabled value={sBpName} />
          </LabelGroup>

          <LabelGroup>
            <label>Relationship</label>
            <TextInput disabled value={attr.relationshipType} />
          </LabelGroup>

          <div style={{ marginLeft : '8px', marginRight : '8px', whiteSpace : 'normal' }}>
            {this.getDesc( attr )}
          </div>

        </Flex>
        
      </Section>
    ) 
  }
}

export default connect( ( state ) => { 
  return { 
    blueprints : state.blueprints,
  }
} )( ParentRelationshipAttributeEditor )
